import { ApplicationRef, EnvironmentInjector, Injectable, createComponent, inject } from '@angular/core';

import { ConfirmModal } from './confirm-modal';
import { ConfirmOptions } from './models/modal.model';

/**
 * Imperative modal API. Renders the universal modal shell without the caller
 * writing any template — ideal for the "click → ask → act" confirmation flow
 * (info / warning / destructive, including type-to-confirm).
 *
 *   const ok = await this.modal.confirm({
 *     kind: 'destructive', destructive: true,
 *     title: 'Delete profile?', message: '…', confirmWord: profile.id,
 *   });
 *   if (ok) { … }
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);

  /** Resolves `true` if the user confirms, `false` on any dismissal. */
  confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const ref = createComponent(ConfirmModal, { environmentInjector: this.envInjector });
      ref.setInput('options', options);

      const sub = ref.instance.result.subscribe((ok: boolean) => {
        resolve(ok);
        // Defer teardown so the closing animation/microtasks settle first.
        queueMicrotask(() => {
          sub.unsubscribe();
          this.appRef.detachView(ref.hostView);
          ref.destroy();
        });
      });

      this.appRef.attachView(ref.hostView);
      document.body.appendChild(ref.location.nativeElement);
    });
  }
}
